import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { isDocente, type User } from "@/types";
import { Spinner } from "@/components/ui/Spinner";

// Public
import Home from "@/pages/public/Home";
import TestAccess from "@/pages/public/TestAccess";
import TestComplete from "@/pages/public/TestComplete";

// Student
import StudentLogin from "@/pages/student/Login";
import AuthVerify from "@/pages/student/AuthVerify";
import StudentDashboard from "@/pages/student/Dashboard";
import TestRunner from "@/pages/student/TestRunner";
import Results from "@/pages/student/Results";

// Admin
import AdminLogin from "@/pages/admin/Login";
import AdminResetPassword from "@/pages/admin/ResetPassword";
import AdminDashboard from "@/pages/admin/Dashboard";
import TestList from "@/pages/admin/TestList";
import TestEditor from "@/pages/admin/TestEditor";
import QuestionManager from "@/pages/admin/QuestionManager";
import ResultsOverview from "@/pages/admin/ResultsOverview";
import StudentAnswers from "@/pages/admin/StudentAnswers";
import Students from "@/pages/admin/Students";
import Tags from "@/pages/admin/Tags";
import SendCredentials from "@/pages/admin/SendCredentials";
import WhatsAppSetup from "@/pages/admin/WhatsAppSetup";
import Campaigns from "@/pages/admin/Campaigns";
import Settings from "@/pages/admin/Settings";
import Register from "@/pages/public/Register";
import { AdminLayout } from "@/components/admin/AdminLayout";

// Errors
import LinkScaduto from "@/pages/errors/LinkScaduto";
import TestChiuso from "@/pages/errors/TestChiuso";
import TestGiaCompletato from "@/pages/errors/TestGiaCompletato";
import SessioneScaduta from "@/pages/errors/SessioneScaduta";
import NotFound from "@/pages/errors/NotFound";

// ─── Auth hook (semplice, no context — il guard rimane montato fra route
//     fratelli, quindi /me viene chiamato una sola volta per sessione di
//     navigazione protetta) ────────────────────────────────────────────────

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "auth"; user: User };

function useAuthMe(): AuthState {
  const [state, setState] = useState<AuthState>(() =>
    localStorage.getItem("jwt") ? { status: "loading" } : { status: "anon" },
  );

  useEffect(() => {
    if (state.status !== "loading") return;
    api.get<{ user: User }>("/auth/me")
      .then((r) => setState({ status: "auth", user: r.user }))
      .catch(() => {
        localStorage.removeItem("jwt");
        setState({ status: "anon" });
      });
  }, [state.status]);

  return state;
}

function FullScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size={32} />
    </div>
  );
}

function StudentGuard() {
  const auth = useAuthMe();
  const loc = useLocation();
  if (auth.status === "loading") return <FullScreen />;
  if (auth.status === "anon") {
    return <Navigate to="/accedi" replace state={{ from: loc.pathname }} />;
  }
  return <Outlet />;
}

function AdminGuard() {
  const auth = useAuthMe();
  if (auth.status === "loading") return <FullScreen />;
  if (auth.status === "anon") return <Navigate to="/admin" replace />;
  if (!isDocente(auth.user.role)) return <Navigate to="/admin" replace />;
  // Layout admin (sidebar + Outlet) avvolge tutte le route admin guardate.
  return <AdminLayout user={auth.user} />;
}

// ─── Routes ───────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/test/:slug" element={<TestAccess />} />
      <Route path="/test/:slug/grazie" element={<TestComplete />} />
      <Route path="/auth/verify" element={<AuthVerify />} />
      <Route path="/registrati/:slug" element={<Register />} />

      {/* Student — /accedi e dashboard sono guardati, il quiz runner e i
          risultati sono pubblici (sicurezza via UUID submissionId): questo
          permette il flusso anonimo end-to-end e il "magic redirect" dopo
          /api/access/:token che porta direttamente al runner senza che
          il guard intercetti l'utente. */}
      <Route path="/accedi" element={<StudentLogin />} />
      <Route path="/area/test/:submissionId" element={<TestRunner />} />
      <Route path="/area/risultati/:submissionId" element={<Results />} />
      <Route element={<StudentGuard />}>
        <Route path="/area" element={<StudentDashboard />} />
      </Route>

      {/* Admin — /admin è l'ingresso pubblico (login), il resto è guardato */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route element={<AdminGuard />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/studenti" element={<Students />} />
        <Route path="/admin/contatti" element={<Students />} />
        <Route path="/admin/tag" element={<Tags />} />
        <Route path="/admin/campagne" element={<Campaigns />} />
        <Route path="/admin/whatsapp" element={<WhatsAppSetup />} />
        <Route path="/admin/tests" element={<TestList />} />
        <Route path="/admin/tests/:id/edit" element={<TestEditor />} />
        <Route path="/admin/tests/:id/domande" element={<QuestionManager />} />
        <Route path="/admin/tests/:id/invio" element={<SendCredentials />} />
        <Route path="/admin/tests/:id/risultati" element={<ResultsOverview />} />
        <Route path="/admin/tests/:id/risposte" element={<StudentAnswers />} />
        <Route path="/admin/impostazioni" element={<Settings />} />
      </Route>

      {/* Errors */}
      <Route path="/errore/link-scaduto" element={<LinkScaduto />} />
      <Route path="/errore/test-chiuso" element={<TestChiuso />} />
      <Route path="/errore/test-completato" element={<TestGiaCompletato />} />
      <Route path="/errore/sessione-scaduta" element={<SessioneScaduta />} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
